import {useCachedAudio, useEventListener} from '@coolcolduk/ui-common-hook';
import {useCallback, useEffect, useRef} from 'react';

export interface GlobalSoundHijackerOptions {
  clickSoundUrl?: string;
  sliderSoundUrl?: string;
  dropdownSoundUrl?: string;
  checkboxSoundUrl?: string;
  switchSoundUrl?: string;
  soundVolume?: number;
  enabled?: boolean;
}

export default function useGlobalSoundHijacker(options: GlobalSoundHijackerOptions = {}) {
  const {
    clickSoundUrl = '/click.mp3',
    sliderSoundUrl = '/slide.mp3',
    dropdownSoundUrl = '/dropdown.mp3',
    checkboxSoundUrl = '/check.mp3',
    switchSoundUrl = '/switch.mp3',
    soundVolume = 30,
    enabled = true,
  } = options;
  const isEnabledRef = useRef(false);
  const lastSliderSoundTime = useRef(0);
  const isDraggingSlider = useRef(false);

  // Use cached audio for different interaction types
  const clickAudio = useCachedAudio({
    sound: clickSoundUrl,
    volume: enabled && soundVolume > 0 ? soundVolume / 100 : 0,
  });

  const sliderAudio = useCachedAudio({
    sound: sliderSoundUrl,
    volume: enabled && soundVolume > 0 ? soundVolume / 100 : 0,
  });

  const dropdownAudio = useCachedAudio({
    sound: dropdownSoundUrl,
    volume: enabled && soundVolume > 0 ? soundVolume / 100 : 0,
  });

  const checkboxAudio = useCachedAudio({
    sound: checkboxSoundUrl,
    volume: enabled && soundVolume > 0 ? soundVolume / 100 : 0,
  });

  const switchAudio = useCachedAudio({
    sound: switchSoundUrl,
    volume: enabled && soundVolume > 0 ? soundVolume / 100 : 0,
  });

  useEffect(() => {
    if (!enabled || soundVolume === 0) {
      isEnabledRef.current = false;
      return undefined;
    }

    isEnabledRef.current = true;
  }, [enabled, soundVolume]);

  const playSound = useCallback((audioHook: ReturnType<typeof useCachedAudio>) => {
    if (!isEnabledRef.current || audioHook.isSilent) return;
    audioHook.play();
  }, []);

  // Hijack common UI interactions
  const handleClick = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;

      // Only play sound for interactive elements
      if (
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.tagName === 'A' ||
        target.closest('a') ||
        target.getAttribute('role') === 'button' ||
        target.closest('[role="button"]') ||
        target.classList.contains('MuiButton-root') ||
        target.closest('.MuiButton-root') ||
        target.classList.contains('MuiIconButton-root') ||
        target.closest('.MuiIconButton-root')
      ) {
        playSound(clickAudio);
      }
    },
    [playSound, clickAudio],
  );

  const handleSelectChange = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'SELECT' ||
        target.closest('select') ||
        target.classList.contains('MuiSelect-select') ||
        target.closest('.MuiSelect-select') ||
        target.classList.contains('MuiMenuItem-root') ||
        target.closest('.MuiMenuItem-root')
      ) {
        playSound(dropdownAudio);
      }
    },
    [playSound, dropdownAudio],
  );

  // Handler for slider start of drag
  const handleSliderStart = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const isSlider =
      target.classList.contains('MuiSlider-thumb') ||
      target.closest('.MuiSlider-thumb') ||
      target.classList.contains('MuiSlider-root') ||
      target.closest('.MuiSlider-root') ||
      target.classList.contains('MuiSlider-track') ||
      target.closest('.MuiSlider-track');

    if (isSlider) {
      isDraggingSlider.current = true;
    }
  }, []);

  // Handler for slider end of drag
  const handleSliderEnd = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const isSlider =
      target.classList.contains('MuiSlider-thumb') ||
      target.closest('.MuiSlider-thumb') ||
      target.classList.contains('MuiSlider-root') ||
      target.closest('.MuiSlider-root') ||
      target.classList.contains('MuiSlider-track') ||
      target.closest('.MuiSlider-track');

    if (isSlider) {
      isDraggingSlider.current = false;
    }
  }, []);

  // Handler for slider input events (actual value changes) with throttling
  const handleSliderInput = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      const isSlider =
        target.classList.contains('MuiSlider-thumb') ||
        target.closest('.MuiSlider-thumb') ||
        target.classList.contains('MuiSlider-root') ||
        target.closest('.MuiSlider-root') ||
        target.classList.contains('MuiSlider-track') ||
        target.closest('.MuiSlider-track');
      if (isSlider && isDraggingSlider.current) {
        const now = Date.now();
        // Throttle slider sound to play at most once every 50ms for better responsiveness
        if (now - lastSliderSoundTime.current > 100) {
          playSound(sliderAudio);
          lastSliderSoundTime.current = now;
        }
      }
    },
    [playSound, sliderAudio],
  );

  const handleCheckboxChange = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        (target as HTMLInputElement).type === 'checkbox' ||
        target.closest('input[type="checkbox"]') ||
        target.classList.contains('MuiCheckbox-root') ||
        target.closest('.MuiCheckbox-root')
      ) {
        playSound(checkboxAudio);
      }
    },
    [playSound, checkboxAudio],
  );

  const handleSwitchChange = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('MuiSwitch-root') || target.closest('.MuiSwitch-root')) {
        playSound(switchAudio);
      }
    },
    [playSound, switchAudio],
  );

  // Special handler for MUI Select dropdowns
  const handleSelectClick = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target.classList.contains('MuiSelect-select') ||
        target.closest('.MuiSelect-select') ||
        target.classList.contains('MuiSelect-icon') ||
        target.closest('.MuiSelect-icon')
      ) {
        playSound(dropdownAudio);
      }
    },
    [playSound, dropdownAudio],
  );

  // Add event listeners using useEventListener
  // Note: Handlers check isEnabledRef.current internally, so they won't play sounds when disabled
  useEventListener(document, 'click', handleClick);
  useEventListener(document, 'click', handleSelectClick);
  useEventListener(document, 'change', handleSelectChange);
  useEventListener(document, 'change', handleCheckboxChange);
  useEventListener(document, 'change', handleSwitchChange);

  // Slider-specific listeners for proper sliding behavior
  useEventListener(document, 'mousedown', handleSliderStart);
  useEventListener(document, 'touchstart', handleSliderStart);
  useEventListener(document, 'mouseup', handleSliderEnd);
  useEventListener(document, 'touchend', handleSliderEnd);
  useEventListener(document, 'input', handleSliderInput);
  useEventListener(document, 'mousemove', handleSliderInput);
  useEventListener(document, 'touchmove', handleSliderInput);

  return {
    isEnabled: isEnabledRef.current,
  };
}
